import { Card, Icon, Stack, Typography } from "@storoey/sui";
import { createSignal, Match, Show, Switch } from "solid-js";
import styles from "./FileDropZone.module.scss";

type FormatBytesOptions = {
  binary?: boolean; // true = use 1024 base (KiB, MiB...), false = use 1000 base (kB, MB...)
  decimals?: number; // number of decimal places
};

export function formatBytes(
  bytes: number,
  { binary = false, decimals = 2 }: FormatBytesOptions = {}
): string {
  if (bytes === 0) return "0 Bytes";

  const base = binary ? 1024 : 1000;
  const units = binary
    ? ["Bytes", "KiB", "MiB", "GiB", "TiB", "PiB"]
    : ["Bytes", "kB", "MB", "GB", "TB", "PB"];

  const i = Math.floor(Math.log(bytes) / Math.log(base));
  const value = bytes / Math.pow(base, i);

  return `${parseFloat(value.toFixed(decimals))} ${units[i]}`;
}

export const FileDropZone = (props: {
  accept?: string;
  multiple?: boolean;
  label?: string;
  file?: File;
  onFilesSelected: (files: FileList | null) => void;
  controller?: {
    reset: () => void;
  };
  isLoading?: boolean;
}) => {
  const [isDragging, setIsDragging] = createSignal(false);
  const [fileInfo, setFileInfo] = createSignal<File | null>(props.file ?? null);
  let fileInputRef: HTMLInputElement | undefined;

  const clear = () => {
    if (fileInputRef) fileInputRef.value = "";
    setFileInfo(null);
    props.onFilesSelected(null);
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      updateFileInfo(files);
      props.onFilesSelected(files);
    }
  };

  const handleChange = (e: Event) => {
    const target = e.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      updateFileInfo(target.files);
      props.onFilesSelected(target.files);
    }
  };

  const updateFileInfo = (files: FileList) => {
    if (files.length > 1) {
      // notify("Velg bare én fil", { type: "error" });
      return;
    }

    const file = Array.from(files)[0];
    setFileInfo(file);
  };

  // Attach reset method to external controller
  if (props.controller) {
    props.controller.reset = clear;
  }

  return (
    <div>
      <Stack gap="4px">
        {fileInfo() && (
          <Card>
            <div class={styles["file"]}>
              <div class={styles["content"]}>
                <Stack gap="4px">
                  <Typography>{fileInfo()!.name}</Typography>
                  <div class={styles["info"]}>
                    <Typography>{formatBytes(fileInfo()!.size)}</Typography>
                    <Icon>pen_size_1</Icon>
                    <Typography>
                      {new Date(fileInfo()!.lastModified).toLocaleString(
                        navigator.language,
                        {
                          year: "numeric",
                          month: "short",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        }
                      )}
                    </Typography>
                  </div>
                </Stack>
              </div>
              <div class={styles["actions"]}>
                <button type="button" class={styles["remove"]} onclick={clear}>
                  <Icon>close</Icon>
                </button>
              </div>
            </div>
          </Card>
        )}
        <Card>
          <div class={styles["container"]}>
            {props.label && <Typography as="label">{props.label}</Typography>}

            <Switch>
              <Match when={props.isLoading}>
                <Show when={props.isLoading}>
                  <div class={styles["drop-zone"]}>
                    <div class={styles["spin"]}>
                      <Icon>progress_activity</Icon>
                    </div>
                  </div>
                </Show>
              </Match>
              <Match when={!props.isLoading}>
                <div
                  classList={{
                    [styles["drop-zone"]]: true,
                    [styles["dragging"]]: isDragging(),
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={() => setIsDragging(false)}
                  onDrop={handleDrop}
                  onClick={() => document.getElementById("fileInput")?.click()}
                >
                  <span class="material-symbols-rounded">upload</span>
                  <Typography>
                    Dra og slipp filer her, eller klikk for å velge
                  </Typography>
                </div>
              </Match>
            </Switch>

            <input
              ref={(el) => (fileInputRef = el)}
              id="fileInput"
              type="file"
              accept={props.accept}
              multiple={props.multiple}
              onChange={handleChange}
              class={styles["input"]}
              disabled={props.isLoading}
            />
          </div>
        </Card>
      </Stack>
    </div>
  );
};
